/**
 * Sistema de logging del cliente independiente de Sentry
 *
 * Guarda logs en localStorage para debugging cuando:
 * - Los requests no llegan al backend (CORS, firewall, etc.)
 * - Errores 400 que no aparecen en PM2
 * - Necesitas ver qué está enviando el cliente exactamente
 */

import { isSensitiveApiPath, redactSensitiveFields } from '@/lib/pii';

interface LogEntry {
  timestamp: string;
  type: 'request' | 'response' | 'error' | 'info';
  level: 'info' | 'warn' | 'error';
  method?: string;
  url?: string;
  status?: number;
  requestData?: any;
  responseData?: any;
  errorMessage?: string;
  userEmail?: string;
  userId?: string;
  browserInfo?: {
    userAgent: string;
    language: string;
    online: boolean;
  };
}

/** Forma mínima de un error de axios que el logger necesita leer. */
interface ApiErrorLike {
  message?: string;
  config?: { data?: unknown; method?: string; url?: string };
  response?: { status?: number; data?: unknown };
}

class ClientLogger {
  private maxLogs = 100; // Máximo de logs a guardar
  private storageKey = 'client_api_logs';
  private enabled = true;

  constructor() {
    // Habilitar solo en desarrollo o con flag específico.
    // Guard de SSR: este módulo se instancia en el build de prerender (Node),
    // donde no existe localStorage.
    this.enabled =
      import.meta.env.DEV ||
      import.meta.env.VITE_CLIENT_LOGGING === 'true' ||
      (typeof localStorage !== 'undefined' &&
        localStorage.getItem('enable_client_logging') === 'true');
  }

  /**
   * Obtener información del navegador
   */
  private getBrowserInfo() {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      online: navigator.onLine,
    };
  }

  /**
   * Obtener info del usuario actual
   */
  private getUserInfo() {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return {
          userId: user._id || user.id,
          userEmail: user.email,
        };
      }
    } catch (error) {
      // Ignorar errores de parsing
    }
    return {};
  }

  /**
   * Guardar log en localStorage
   */
  private saveLog(entry: LogEntry) {
    if (!this.enabled) return;

    try {
      const logs = this.getLogs();

      // Agregar info del usuario y navegador
      const enrichedEntry = {
        ...entry,
        ...this.getUserInfo(),
        browserInfo: this.getBrowserInfo(),
      };

      logs.unshift(enrichedEntry);

      // Limitar cantidad de logs
      if (logs.length > this.maxLogs) {
        logs.splice(this.maxLogs);
      }

      localStorage.setItem(this.storageKey, JSON.stringify(logs));
    } catch (error) {
      // Si localStorage está lleno, limpiar logs viejos
      console.warn('Error guardando log, limpiando logs antiguos', error);
      this.clearLogs();
    }
  }

  /**
   * Log de request saliente
   */
  logRequest(method: string, url: string, data?: any, headers?: any) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      type: 'request',
      level: 'info',
      method: method.toUpperCase(),
      url,
      requestData: data ? (typeof data === 'string' ? data : JSON.stringify(data).substring(0, 2000)) : undefined,
    };

    this.saveLog(entry);

    // También loguear en consola en desarrollo
    if (import.meta.env.DEV) {
      console.log(`📤 API Request: ${method.toUpperCase()} ${url}`, data);
    }
  }

  /**
   * Log de response exitosa
   */
  logResponse(method: string, url: string, status: number, data?: any) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      type: 'response',
      level: 'info',
      method: method.toUpperCase(),
      url,
      status,
      responseData: data ? JSON.stringify(data).substring(0, 2000) : undefined,
    };

    this.saveLog(entry);

    if (import.meta.env.DEV) {
      console.log(`📥 API Response: ${method.toUpperCase()} ${url} - ${status}`, data);
    }
  }

  /**
   * Log de error de API
   */
  logError(method: string, url: string, error: ApiErrorLike) {
    const redact = isSensitiveApiPath(url);
    const requestData = error.config?.data;
    const responseData = error.response?.data;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      type: 'error',
      level: 'error',
      method: method?.toUpperCase(),
      url,
      status: error.response?.status,
      requestData: requestData
        ? JSON.stringify(
            redact ? redactSensitiveFields(requestData) : requestData
          ).substring(0, 2000)
        : undefined,
      responseData: responseData
        ? JSON.stringify(
            redact ? redactSensitiveFields(responseData) : responseData
          ).substring(0, 2000)
        : undefined,
      errorMessage: error.message,
    };

    this.saveLog(entry);

    if (import.meta.env.DEV) {
      console.error(
        `❌ API Error: ${method?.toUpperCase()} ${url}`,
        redact ? { message: error.message, status: error.response?.status } : error
      );
    }

    // Envío automático al backend cuando hay un error
    // Esto es NO bloqueante y transparente para el usuario
    this.autoUploadOnError(error);
  }

  /**
   * Log genérico
   */
  log(message: string, data?: any) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      type: 'info',
      level: 'info',
      errorMessage: message,
      requestData: data,
    };

    this.saveLog(entry);
  }

  /**
   * Obtener todos los logs
   */
  getLogs(): LogEntry[] {
    try {
      const logsStr = localStorage.getItem(this.storageKey);
      if (!logsStr) return [];
      return JSON.parse(logsStr);
    } catch (error) {
      console.error('Error leyendo logs:', error);
      return [];
    }
  }

  /**
   * Obtener logs como texto para copiar/enviar
   */
  getLogsAsText(): string {
    const logs = this.getLogs();
    return logs.map(log => {
      return `[${log.timestamp}] ${log.type.toUpperCase()} - ${log.method || ''} ${log.url || ''}
Status: ${log.status || 'N/A'}
Error: ${log.errorMessage || 'N/A'}
Request: ${log.requestData || 'N/A'}
Response: ${log.responseData || 'N/A'}
User: ${log.userEmail || 'N/A'} (${log.userId || 'N/A'})
Browser: ${log.browserInfo?.userAgent || 'N/A'}
---`;
    }).join('\n');
  }

  /**
   * Exportar logs como JSON para descargar
   */
  downloadLogs() {
    const logs = this.getLogs();
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `client-logs-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Limpiar todos los logs
   */
  clearLogs() {
    localStorage.removeItem(this.storageKey);
  }

  /**
   * Enviar logs al backend para análisis
   */
  async uploadLogsToBackend() {
    try {
      const logs = this.getLogs();
      const userInfo = this.getUserInfo();

      // Importar dinámicamente para evitar dependencia circular
      const { default: api } = await import('@/services/api');

      await api.post('/debug/client-logs', {
        logs,
        timestamp: new Date().toISOString(),
        userEmail: userInfo.userEmail,
        userId: userInfo.userId,
      });

      console.log('✅ Logs enviados al backend');
      return true;
    } catch (error) {
      console.error('❌ Error enviando logs al backend:', error);
      return false;
    }
  }

  /**
   * Envío automático de logs cuando ocurre un error
   * Esto es NO bloqueante y transparente para el usuario
   *
   * Solo envía si:
   * 1. Hay errores en los logs
   * 2. No se envió recientemente (para evitar spam)
   * 3. El error es crítico (4xx, 5xx o network error)
   */
  private lastAutoUpload: number = 0;
  private autoUploadThrottleMs = 30000; // 30 segundos entre envíos automáticos

  private async autoUploadOnError(error: ApiErrorLike) {
    // Solo en producción o si está explícitamente habilitado
    const isProduction = import.meta.env.PROD;
    if (!isProduction && !this.enabled) {
      return;
    }

    // Verificar si es un error que vale la pena reportar
    const status = error.response?.status;
    const isNetworkError = !error.response;

    // Solo reportar errores 4xx, 5xx o errores de red
    // Ignorar 401 porque son esperados (token expirado)
    if (!isNetworkError && status === 401) {
      return;
    }

    // Throttling: evitar enviar logs muy seguido
    const now = Date.now();
    if (now - this.lastAutoUpload < this.autoUploadThrottleMs) {
      console.log('⏳ Auto-upload throttled, esperando...');
      return;
    }

    this.lastAutoUpload = now;

    // Enviar en background, no bloquear la UI
    try {
      const logs = this.getLogs();
      const userInfo = this.getUserInfo();

      // Usar fetch nativo para evitar dependencia circular con axios
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

      // Fire and forget - no esperamos respuesta
      fetch(`${API_URL}/debug/client-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          logs,
          timestamp: new Date().toISOString(),
          userEmail: userInfo.userEmail,
          userId: userInfo.userId,
          autoUploaded: true, // Marcar que fue envío automático
          triggerError: {
            method: error.config?.method,
            url: error.config?.url,
            status: error.response?.status,
            message: error.message,
          },
        }),
      }).catch(err => {
        // Ignorar errores de envío para no crear loop infinito
        console.debug('Auto-upload failed (silent):', err.message);
      });

      console.log('📤 Logs enviados automáticamente al backend');
    } catch (error) {
      // Silenciar errores para no interferir con la aplicación
      console.debug('Auto-upload error (silent):', error);
    }
  }

  /**
   * Habilitar/deshabilitar logging
   */
  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    localStorage.setItem('enable_client_logging', enabled ? 'true' : 'false');
  }

  /**
   * Verificar si está habilitado
   */
  isEnabled(): boolean {
    return this.enabled;
  }
}

// Singleton
export const clientLogger = new ClientLogger();

// Exponer globalmente para debugging en consola
if (typeof window !== 'undefined') {
  (window as any).clientLogger = clientLogger;
}

export default clientLogger;
